/* ============================================================
 * 个人中心聚合 store
 *
 * 5 分钟内复用 `/app/profile/overview` 结果，避免每次切换 Tab 都拉。
 * 设置页保存成功后调用 `refresh()` 强制更新。
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getProfileOverview } from '@/api/profile'
import type { ProfileOverview } from '@/types/profile'

const TTL_MS = 5 * 60 * 1000

export const useProfileStore = defineStore('profile', () => {
  const data = ref<ProfileOverview | null>(null)
  const fetchedAt = ref(0)
  const loading = ref(false)
  const errorMessage = ref('')

  const isStale = computed(() => Date.now() - fetchedAt.value > TTL_MS)
  const user = computed(() => data.value?.user ?? null)
  const stats = computed(() => data.value?.stats ?? null)

  async function load(force = false): Promise<void> {
    if (loading.value) return
    if (!force && data.value && !isStale.value) return
    loading.value = true
    errorMessage.value = ''
    try {
      const resp = await getProfileOverview()
      data.value = resp.data
      fetchedAt.value = Date.now()
    } catch (err) {
      errorMessage.value = err instanceof Error ? err.message : '加载个人中心失败'
    } finally {
      loading.value = false
    }
  }

  function reset(): void {
    data.value = null
    fetchedAt.value = 0
    errorMessage.value = ''
  }

  function refresh(): Promise<void> {
    return load(true)
  }

  return { data, loading, errorMessage, user, stats, load, refresh, reset }
})
